// TCIO Messenger P3 v1 - Messages UI (charter v26 + ratified 2026-08-16)
// Desktop: list + conversation panes. Mobile (<768px): single pane w/ back.
// Polling v1 (threads 10s, open thread 5s). Suggest-for-Chart in client threads.
// Review queue panel for approvers (superuser / manage_clinical_users).
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { tokens } from './theme';
import DictatePanel from './DictatePanel';
import { threadLabel } from './threadLabel';

const API = 'https://tcio-api.onrender.com';
const PRIORITY_COLORS = { urgent: tokens.danger, priority: tokens.warning, normal: null };

function timeAgo(iso) {
  if (!iso) return '';
  const d = new Date(iso), now = new Date();
  const mins = Math.floor((now - d) / 60000);
  if (mins < 1) return 'now';
  if (mins < 60) return mins + 'm';
  if (mins < 1440) return Math.floor(mins / 60) + 'h';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default function Messages({ user, nav }) {
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);       // thread object
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState('');
  const [priority, setPriority] = useState('normal');
  const [needsResp, setNeedsResp] = useState(false);
  const [searchQ, setSearchQ] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [showQueue, setShowQueue] = useState(false);
  const [queueTab, setQueueTab] = useState('pending');
  const [revBusy, setRevBusy] = useState(null); // suggestion id in flight
  const [queue, setQueue] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [newType, setNewType] = useState('dm');
  const [newMembers, setNewMembers] = useState([]);
  const [newTitle, setNewTitle] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [sending, setSending] = useState(false);
  const [dictOpen, setDictOpen] = useState(false);
  const [reads, setReads] = useState([]);
  const bottomRef = useRef(null);

  const perms = user.emr_permissions || {};
  const isApprover = perms.superuser === true || !!perms.manage_clinical_users ||
    ['ceo', 'admin'].includes(user.role);
  const canManageGroups = perms.superuser === true || !!perms.manage_clinical_users ||
    !!perms.manage_messenger_groups || ['ceo', 'admin'].includes(user.role);
  const isSuper = perms.superuser === true || ['ceo', 'admin'].includes(user.role);
  const [facFilter, setFacFilter] = useState('ALL');
  const [peopleQ, setPeopleQ] = useState('');
  const facNames = Array.from(new Set(allUsers.map(u => u.facility_name).filter(Boolean))).sort();
  const peopleNeedle = peopleQ.trim().toLowerCase();
  const visibleUsers = (facFilter === 'ALL' ? allUsers : allUsers.filter(u => u.facility_name === facFilter))
    .filter(u => !peopleNeedle || [u.name, u.first_name, u.last_name, u.email, u.role, u.facility_name].some(v => String(v || '').toLowerCase().includes(peopleNeedle)));
  const facDropdown = (<>
    <input value={peopleQ} onChange={e => setPeopleQ(e.target.value)} placeholder="Search by name, role or facility…" autoFocus
      style={{ width: '100%', boxSizing: 'border-box', border: '1px solid ' + tokens.border, borderRadius: tokens.radiusControl, padding: '7px 9px', fontSize: 13, marginBottom: 8, fontFamily: tokens.font, color: tokens.ink }} />
    {isSuper && facNames.length > 1 ? (
    <select value={facFilter} onChange={e => setFacFilter(e.target.value)}
      style={{ width: '100%', boxSizing: 'border-box', border: '1px solid ' + tokens.border,
        borderRadius: tokens.radiusControl, padding: '6px 8px', fontSize: 12, marginBottom: 8,
        fontFamily: tokens.font, color: tokens.textSecondary }}>
      <option value="ALL">All facilities</option>
      {facNames.map(f => <option key={f} value={f}>{f}</option>)}
    </select>) : null}
  </>);
  const [showMembers, setShowMembers] = useState(false);
  const [members, setMembers] = useState([]);
  const loadMembers = async (tid) => {
    try { const r = await axios.get(API + '/api/msg/threads/' + tid + '/members'); setMembers(r.data); }
    catch (e) { console.error(e); }
    if (!allUsers.length) {
      try { const ru = await axios.get(API + '/api/msg/colleagues'); setAllUsers((ru.data || []).filter(u => u.id !== user.id)); }
      catch (e) { console.error(e); }
    }
  };
  const addMember = async (uid) => {
    try {
      await axios.post(API + '/api/msg/threads/' + active.id + '/members', { member_ids: [uid] });
      loadMembers(active.id);
    } catch (e) { alert(e.response?.data?.error || e.message); }
  };
  const removeMember = async (uid, name) => {
    if (!window.confirm('Remove ' + name + ' from this conversation? Their past messages will remain.')) return;
    try {
      await axios.delete(API + '/api/msg/threads/' + active.id + '/members/' + uid);
      loadMembers(active.id);
    } catch (e) { alert(e.response?.data?.error || e.message); }
  };
  const toggleMute = async (cur) => {
    try {
      await axios.post(API + '/api/msg/threads/' + active.id + '/mute', { muted: !cur });
      loadMembers(active.id);
    } catch (e) { alert(e.response?.data?.error || e.message); }
  };

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const q = searchQ.trim();
    if (q.length < 2) { setSearchResults([]); return; }
    const h = setTimeout(async () => {
      try { const r = await axios.get(API + '/api/msg/search', { params: { q } }); setSearchResults(r.data); }
      catch (e) { console.error(e); }
    }, 300);
    return () => clearTimeout(h);
  }, [searchQ]);

  // Deep-link handoff: EMR's Message Care Team stashes a thread id; open it once the list has it.
  useEffect(() => {
    const tid = sessionStorage.getItem('tcio_open_thread');
    if (!tid || !threads.length) return;
    const t = threads.find(x => x.id === tid);
    if (t) { sessionStorage.removeItem('tcio_open_thread'); setShowQueue(false); setActive(t); }
  }, [threads]);

  const loadThreads = useCallback(async () => {
    try { const r = await axios.get(API + '/api/msg/threads'); setThreads(r.data); }
    catch (e) { console.error(e); }
  }, []);

  const loadMessages = useCallback(async (tid) => {
    try {
      const [r, rr] = await Promise.all([
        axios.get(API + '/api/msg/threads/' + tid + '/messages'),
        axios.get(API + '/api/msg/threads/' + tid + '/reads'),
      ]);
      setMessages(r.data); setReads(rr.data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { loadThreads(); const iv = setInterval(loadThreads, 10000); return () => clearInterval(iv); }, [loadThreads]);

  useEffect(() => {
    if (!active) return;
    setShowMembers(false);
    loadMessages(active.id);
    axios.post(API + '/api/msg/threads/' + active.id + '/read').then(loadThreads).catch(() => {});
    const iv = setInterval(() => loadMessages(active.id), 5000);
    return () => clearInterval(iv);
  }, [active, loadMessages, loadThreads]);

  useEffect(() => { if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: 'smooth' }); }, [messages.length]);

  const openNew = async () => {
    setShowNew(true); setNewType('dm'); setNewMembers([]); setNewTitle('');
    if (!allUsers.length) {
      try { const r = await axios.get(API + '/api/msg/colleagues'); setAllUsers((r.data || []).filter(u => u.id !== user.id)); }
      catch (e) { console.error(e); }
    }
  };

  const createThread = async () => {
    try {
      const payload = { thread_type: newType, member_ids: newMembers };
      if (newType === 'group') payload.title = newTitle || 'Group';
      const r = await axios.post(API + '/api/msg/threads', payload);
      setShowNew(false);
      await loadThreads();
      const t = (await axios.get(API + '/api/msg/threads')).data.find(x => x.id === r.data.id);
      if (t) setActive(t);
    } catch (e) { alert(e.response?.data?.error || e.message); }
  };

  const send = async () => {
    if (!body.trim() || !active || sending) return;
    setSending(true);
    try {
      await axios.post(API + '/api/msg/threads/' + active.id + '/messages',
        { body: body.trim(), priority, needs_response: needsResp });
      setBody(''); setPriority('normal'); setNeedsResp(false);
      await loadMessages(active.id);
    } catch (e) { alert(e.response?.data?.error || e.message); }
    setSending(false);
  };

  const suggestChart = async (msgId) => {
    const reason = window.prompt('Why should this be reviewed for the chart?');
    if (reason === null) return;
    try {
      await axios.post(API + '/api/msg/messages/' + msgId + '/suggest-chart', { reason });
      loadMessages(active.id);
    } catch (e) { alert(e.response?.data?.error || e.message); }
  };

  const loadQueue = async (st) => {
    const status = st || queueTab;
    try { const r = await axios.get(API + '/api/msg/chart-suggestions?status=' + status); setQueue(r.data); }
    catch (e) { console.error(e); }
  };

  const reviewAct = async (sid, action) => {
    if (revBusy) return;
    const note = window.prompt(action === 'approve' ? 'Review note (optional):' : 'Reason (optional):');
    if (note === null) return;
    setRevBusy(sid);
    try {
      const r = await axios.post(API + '/api/msg/chart-suggestions/' + sid + '/' + action, { review_note: note || null });
      if (action === 'approve') {
        const d = r.data || {};
        if (d.warning) alert(d.warning);
        else if (d.note_id && nav && window.confirm(
          'Approved. A draft note was created on ' + (d.patient_name || 'the patient') + "'s chart. Open it now to review and sign?")) {
          sessionStorage.setItem('tcio_open_patient', d.patient_id);
          nav('emr');
          return;
        }
      }
      if (action === 'retract') {
        const d = r.data || {};
        if (d.warning) alert(d.warning);
        else if (d.voided_note_id) alert('Retracted - the draft note on the chart was voided.');
      }
      loadQueue();
    } catch (e) { alert(e.response?.data?.error || e.message); }
    setRevBusy(null);
  };


  const card = { background: tokens.surface, border: '1px solid ' + tokens.border, borderRadius: tokens.radiusCard };
  const showList = !isMobile || (!active && !showQueue);
  const showConvo = !isMobile || (active && !showQueue);

  return (
    <div style={{ display: 'flex', gap: 14, padding: isMobile ? 8 : 18, height: 'calc(100vh - 120px)', fontFamily: tokens.font }}>
      {showList && (
      <div style={{ ...card, width: isMobile ? '100%' : 330, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: 14, borderBottom: '1px solid ' + tokens.borderSoft, display: 'flex', gap: 8 }}>
          <button onClick={openNew} style={{ flex: 1, background: tokens.ai, color: '#fff', border: 'none',
            borderRadius: tokens.radiusControl, padding: '9px 0', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: tokens.font }}>
            New Message</button>
          {isApprover && (
            <button onClick={() => { setShowQueue(true); setActive(null); loadQueue(); }}
              style={{ background: tokens.surfaceSoft, border: '1px solid ' + tokens.border, borderRadius: tokens.radiusControl,
                padding: '9px 12px', fontSize: 12, cursor: 'pointer', color: tokens.textSecondary, fontFamily: tokens.font }}>
              Review</button>)}
        </div>
        <div style={{ padding: '8px 14px', borderBottom: '1px solid ' + tokens.borderSoft }}>
          <input value={searchQ} onChange={e => setSearchQ(e.target.value)} placeholder={'Search messages\u2026'}
            style={{ width: '100%', boxSizing: 'border-box', border: '1px solid ' + tokens.border,
              borderRadius: tokens.radiusControl, padding: '7px 10px', fontSize: 12.5,
              fontFamily: tokens.font, outline: 'none' }} />
        </div>
        <div style={{ overflowY: 'auto', flex: 1 }}>
          {searchQ.trim().length >= 2 && (<>
            {searchResults.length === 0 && <div style={{ padding: 18, textAlign: 'center',
              color: tokens.textMuted, fontSize: 12.5 }}>No matches.</div>}
            {searchResults.map(rm => {
              const th = threads.find(x => x.id === rm.thread_id);
              return (
                <div key={rm.id} onClick={() => { if (th) { setShowQueue(false); setActive(th); } setSearchQ(''); }}
                  style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid ' + tokens.borderSoft }}>
                  <div style={{ fontSize: 12.5, fontWeight: 600, color: tokens.ink }}>
                    {th ? threadLabel(th) : (rm.title || 'Conversation')}</div>
                  <div style={{ fontSize: 12, color: tokens.textSecondary, whiteSpace: 'nowrap',
                    overflow: 'hidden', textOverflow: 'ellipsis' }}>{rm.sender_name}: {rm.body}</div>
                  <div style={{ fontSize: 10.5, color: tokens.textMuted }}>{timeAgo(rm.created_at)}</div>
                </div>);
            })}
          </>)}
          {searchQ.trim().length < 2 && threads.length === 0 && (
            <div style={{ padding: 24, textAlign: 'center', color: tokens.textMuted, fontSize: 13 }}>
              No conversations yet. Start one with New Message.</div>)}
          {searchQ.trim().length < 2 && threads.map(t => (
            <div key={t.id} onClick={() => { setShowQueue(false); setActive(t); }}
              style={{ padding: '12px 14px', cursor: 'pointer', borderBottom: '1px solid ' + tokens.borderSoft,
                background: active && active.id === t.id ? tokens.aiSoft : 'transparent' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                {t.last_needs_response && <span title="Awaiting response"
                  style={{ color: tokens.warning, marginRight: 5, fontSize: 11 }}>{'\u25cf'}</span>}
                <span style={{ fontSize: 13.5, fontWeight: Number(t.unread) > 0 ? 700 : 600, color: tokens.ink }}>
                  {threadLabel(t)}</span>
                <span style={{ fontSize: 11, color: tokens.textMuted }}>{timeAgo(t.last_at)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 3, gap: 8 }}>
                <span style={{ fontSize: 12, color: tokens.textSecondary, whiteSpace: 'nowrap', overflow: 'hidden',
                  textOverflow: 'ellipsis' }}>{t.last_body || 'No messages yet'}</span>
                {Number(t.unread) > 0 && (
                  <span style={{ background: tokens.ai, color: '#fff', borderRadius: 10, fontSize: 10.5,
                    padding: '1px 7px', fontWeight: 700, flexShrink: 0 }}>{t.unread}</span>)}
              </div>
            </div>))}
        </div>
      </div>)}

      {showConvo && !showQueue && (
      <div style={{ ...card, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {!active ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: tokens.textMuted, fontSize: 14 }}>Select a conversation</div>
        ) : (
          <>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid ' + tokens.borderSoft,
              display: 'flex', alignItems: 'center', gap: 10 }}>
              {isMobile && <button onClick={() => setActive(null)} style={{ background: 'none', border: 'none',
                fontSize: 18, cursor: 'pointer', color: tokens.ai, padding: 0 }}>{'<'}</button>}
              <div>
                <div style={{ fontSize: 14.5, fontWeight: 700, color: tokens.ink }}>{threadLabel(active)}</div>
                {active.thread_type === 'client' && (
                  <div style={{ fontSize: 11.5, color: tokens.textSecondary }}>
                    Associated with {active.patient_name} - not part of the chart</div>)}
              </div>
              {active.thread_type !== 'dm' && (
                <button onClick={() => { setShowMembers(v => !v); if (!showMembers) loadMembers(active.id); }}
                  style={{ marginLeft: 'auto', background: showMembers ? tokens.aiSoft : tokens.surface,
                    border: '1px solid ' + tokens.border, borderRadius: 99, padding: '5px 13px',
                    fontSize: 12, fontWeight: 600, cursor: 'pointer', color: tokens.textSecondary,
                    fontFamily: tokens.font }}>Members</button>)}
            </div>
            {showMembers && active.thread_type !== 'dm' && (
              <div style={{ padding: '10px 16px', borderBottom: '1px solid ' + tokens.borderSoft,
                background: tokens.surfaceSoft, maxHeight: 260, overflowY: 'auto' }}>
                {members.map(mb => (
                  <div key={mb.user_id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0' }}>
                    <span style={{ fontSize: 12.5, fontWeight: mb.user_id === user.id ? 700 : 500, color: tokens.ink }}>
                      {mb.name}{mb.user_id === user.id ? ' (you)' : ''}</span>
                    {mb.muted && <span style={{ fontSize: 10.5, color: tokens.textMuted }}>muted</span>}
                    <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                      {mb.user_id === user.id && (
                        <button onClick={() => toggleMute(mb.muted)}
                          style={{ background: 'none', border: '1px solid ' + tokens.border, borderRadius: 6,
                            padding: '2px 9px', fontSize: 11, cursor: 'pointer', color: tokens.textSecondary,
                            fontFamily: tokens.font }}>{mb.muted ? 'Unmute' : 'Mute'}</button>)}
                      {canManageGroups && mb.user_id !== user.id && (
                        <button onClick={() => removeMember(mb.user_id, mb.name)}
                          style={{ background: 'none', border: '1px solid ' + tokens.border, borderRadius: 6,
                            padding: '2px 9px', fontSize: 11, cursor: 'pointer', color: tokens.danger,
                            fontFamily: tokens.font }}>Remove</button>)}
                    </span>
                  </div>))}
                {canManageGroups && (<>
                  {facDropdown}
                  <select value="" onChange={e => e.target.value && addMember(e.target.value)}
                    style={{ marginTop: 8, width: '100%', border: '1px solid ' + tokens.border,
                      borderRadius: tokens.radiusControl, padding: '6px 8px', fontSize: 12,
                      fontFamily: tokens.font, color: tokens.textSecondary }}>
                    <option value="">+ Add a member{'\u2026'}</option>
                    {visibleUsers.filter(u => !members.some(mb => mb.user_id === u.id)).map(u => (
                      <option key={u.id} value={u.id}>{u.first_name} {u.last_name}{u.facility_name ? ' (' + u.facility_name + ')' : ''}</option>))}
                  </select></>)}
                <div style={{ fontSize: 10.5, color: tokens.textMuted, marginTop: 8 }}>
                  Membership is managed by supervisors. Mute to quiet notifications without leaving.</div>
              </div>)}
            <div style={{ flex: 1, overflowY: 'auto', padding: 16, background: tokens.canvas }}>
              {messages.map(m => {
                const mine = m.sender_id === user.id;
                const pc = PRIORITY_COLORS[m.priority];
                return (
                  <div key={m.id} style={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start', marginBottom: 10 }}>
                    <div style={{ maxWidth: '72%' }}>
                      {!mine && <div style={{ fontSize: 11, color: tokens.textMuted, marginBottom: 2 }}>{m.sender_name}</div>}
                      <div style={{ background: mine ? tokens.ai : tokens.surface, color: mine ? '#fff' : tokens.ink,
                        border: mine ? 'none' : '1px solid ' + tokens.border, borderRadius: 12, padding: '8px 12px',
                        fontSize: 13.5, lineHeight: 1.45 }}>
                        {pc && <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                          letterSpacing: '0.06em', color: mine ? '#fff' : pc, marginBottom: 3 }}>{m.priority}</div>}
                      {m.needs_response && <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em',
                        color: mine ? '#fff' : tokens.warning, marginBottom: 3 }}>{'\u21a9'} NEEDS RESPONSE</div>}
                        {m.body}
                        {m.edited_at && <span style={{ fontSize: 10.5, opacity: 0.7 }}> (edited)</span>}
                      </div>
                      <div style={{ display: 'flex', gap: 8, marginTop: 3, justifyContent: mine ? 'flex-end' : 'flex-start' }}>
                        <span style={{ fontSize: 10.5, color: tokens.textMuted }}>
                          {new Date(m.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</span>
                        {active.thread_type === 'client' && !m.suggestion_id && (
                          <span onClick={() => suggestChart(m.id)} style={{ fontSize: 10.5, color: tokens.ai,
                            cursor: 'pointer', fontWeight: 600 }}>Suggest for Chart</span>)}
                        {m.suggestion_status && (
                          <span style={{ fontSize: 10.5, fontWeight: 700, color:
                            m.suggestion_status === 'approved' ? tokens.success :
                            m.suggestion_status === 'pending' ? tokens.warning : tokens.textMuted }}>
                            Chart: {m.suggestion_status}</span>)}
                      </div>
                    </div>
                  </div>);
              })}
              {(() => {
                const last = messages[messages.length - 1];
                if (!last || last.sender_id !== user.id || !reads.length) return null;
                const seen = reads.filter(x => x.last_read_at && new Date(x.last_read_at) >= new Date(last.created_at));
                if (!seen.length) return null;
                const label = reads.length === 1
                  ? 'Read ' + new Date(seen[0].last_read_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
                  : (seen.length === reads.length ? 'Read by all' : 'Read by ' + seen.length + ' of ' + reads.length);
                return <div style={{ textAlign: 'right', fontSize: 10.5, color: tokens.textMuted,
                  marginTop: -4, marginBottom: 6, paddingRight: 4 }}>{label}</div>;
              })()}
              <div ref={bottomRef} />
            </div>
            {dictOpen && (
              <div style={{ padding: '10px 12px 0', borderTop: '1px solid ' + tokens.borderSoft }}>
                <DictatePanel fallbackPath="/api/msg/transcribe" cta="Dictate a message"
                  submitLabel="Use as message" placeholder="Your words appear here - edit before using"
                  onSubmit={(t) => { setBody(t); setDictOpen(false); }} />
              </div>
            )}
            <div style={{ padding: 12, borderTop: dictOpen ? 'none' : '1px solid ' + tokens.borderSoft, display: 'flex', gap: 8 }}>
              <button onClick={() => setDictOpen(o => !o)} title="Dictate"
                style={{ background: dictOpen ? tokens.aiSoft : tokens.surface, border: '1px solid ' + tokens.border,
                  borderRadius: tokens.radiusControl, padding: '0 11px', fontSize: 15, cursor: 'pointer' }}>{'\uD83C\uDF99'}</button>
              <select value={priority} onChange={e => setPriority(e.target.value)}
                style={{ border: '1px solid ' + tokens.border, borderRadius: tokens.radiusControl, fontSize: 12,
                  padding: '0 6px', color: tokens.textSecondary, fontFamily: tokens.font }}>
                <option value="normal">Normal</option>
                <option value="priority">Priority</option>
                <option value="urgent">Urgent</option>
              </select>
              <button onClick={() => setNeedsResp(v => !v)} title="Request a response"
                style={{ background: needsResp ? tokens.warningBg : tokens.surface,
                  color: needsResp ? tokens.warning : tokens.textSecondary,
                  border: '1px solid ' + (needsResp ? tokens.warning : tokens.border),
                  borderRadius: tokens.radiusControl, padding: '0 10px', fontSize: 11.5, fontWeight: 600,
                  cursor: 'pointer', fontFamily: tokens.font, whiteSpace: 'nowrap' }}>Needs reply</button>
              <input value={body} onChange={e => setBody(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Type a message..."
                style={{ flex: 1, minWidth: 0, border: '1px solid ' + tokens.border, borderRadius: tokens.radiusControl,
                  padding: '9px 12px', fontSize: 13.5, fontFamily: tokens.font, outline: 'none' }} />
              <button onClick={send} disabled={sending || !body.trim()}
                style={{ background: tokens.ai, color: '#fff', border: 'none', borderRadius: tokens.radiusControl,
                  padding: '0 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: sending || !body.trim() ? 0.5 : 1,
                  flexShrink: 0, fontFamily: tokens.font }}>Send</button>
            </div>
          </>)}
      </div>)}

      {showQueue && (
      <div style={{ ...card, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '12px 16px', borderBottom: '1px solid ' + tokens.borderSoft,
          display: 'flex', alignItems: 'center', gap: 10 }}>
          {isMobile && <button onClick={() => setShowQueue(false)} style={{ background: 'none', border: 'none',
            fontSize: 18, cursor: 'pointer', color: tokens.ai, padding: 0 }}>{'<'}</button>}
          <div style={{ fontSize: 14.5, fontWeight: 700, color: tokens.ink }}>Chart Inclusion Review</div>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
            {['pending', 'approved'].map(tb => (
              <button key={tb} onClick={() => { setQueueTab(tb); loadQueue(tb); }}
                style={{ background: queueTab === tb ? tokens.ai : tokens.surface,
                  color: queueTab === tb ? '#fff' : tokens.textSecondary,
                  border: '1px solid ' + (queueTab === tb ? tokens.ai : tokens.border),
                  borderRadius: 99, padding: '4px 12px', fontSize: 12, fontWeight: 600,
                  cursor: 'pointer', fontFamily: tokens.font, textTransform: 'capitalize' }}>{tb}</button>
            ))}
          </div>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
          {queue.length === 0 && <div style={{ color: tokens.textMuted, fontSize: 13 }}>No {queueTab} suggestions.</div>}
          {queue.map(s => (
            <div key={s.id} style={{ border: '1px solid ' + tokens.border, borderRadius: tokens.radiusCard,
              padding: 14, marginBottom: 12, background: tokens.surfaceSoft }}>
              <div style={{ fontSize: 12, color: tokens.textSecondary, marginBottom: 6 }}>
                <b style={{ color: tokens.ink }}>{s.patient_name}</b> - written by {s.author_name} - suggested by {s.suggested_by_name}
              </div>
              <div style={{ fontSize: 13.5, color: tokens.ink, background: tokens.surface,
                border: '1px solid ' + tokens.borderSoft, borderRadius: 8, padding: '8px 11px', marginBottom: 8 }}>
                {s.message_body}</div>
              {s.reason && <div style={{ fontSize: 12, color: tokens.textSecondary, marginBottom: 8 }}>Reason: {s.reason}</div>}
              {queueTab === 'pending' ? (<>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => reviewAct(s.id, 'approve')} disabled={!!revBusy}
                  style={{ background: tokens.success, color: '#fff',
                  border: 'none', borderRadius: 7, padding: '6px 14px', fontSize: 12, fontWeight: 600,
                  cursor: revBusy ? 'default' : 'pointer', opacity: revBusy && revBusy !== s.id ? 0.5 : 1,
                  fontFamily: tokens.font }}>{revBusy === s.id ? 'Creating note\u2026' : 'Approve'}</button>
                <button onClick={() => reviewAct(s.id, 'reject')} disabled={!!revBusy}
                  style={{ background: tokens.surface, color: tokens.danger,
                  border: '1px solid ' + tokens.border, borderRadius: 7, padding: '6px 14px', fontSize: 12, fontWeight: 600,
                  cursor: revBusy ? 'default' : 'pointer', opacity: revBusy ? 0.5 : 1, fontFamily: tokens.font }}>Reject</button>
              </div>
              <div style={{ fontSize: 11, color: tokens.textMuted, marginTop: 8 }}>
                Approving creates a draft note on the chart. Review, edit, and sign it in the EMR - your signature is what enters it into the record.</div>
              </>) : (<>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => reviewAct(s.id, 'retract')} disabled={!!revBusy}
                  style={{ background: tokens.surface, color: tokens.danger,
                  border: '1px solid ' + tokens.border, borderRadius: 7, padding: '6px 14px', fontSize: 12, fontWeight: 600,
                  cursor: revBusy ? 'default' : 'pointer', opacity: revBusy && revBusy !== s.id ? 0.5 : 1,
                  fontFamily: tokens.font }}>{revBusy === s.id ? 'Retracting\u2026' : 'Retract'}</button>
              </div>
              <div style={{ fontSize: 11, color: tokens.textMuted, marginTop: 8 }}>
                Retracting voids the draft note if it has not been signed. Signed notes stand on the chart and require an amendment.</div>
              </>)}
            </div>))}
        </div>
      </div>)}

      {showNew && (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(10,34,57,0.45)', display: 'flex',
        alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
        <div style={{ background: tokens.surface, borderRadius: tokens.radiusCard, padding: 22,
          width: 'min(420px, 92vw)', maxHeight: '80vh', overflowY: 'auto' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: tokens.ink, marginBottom: 14 }}>New Message</div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            {(canManageGroups ? ['dm', 'group'] : ['dm']).map(t => (
              <button key={t} onClick={() => { setNewType(t); setNewMembers([]); }}
                style={{ flex: 1, padding: '7px 0', borderRadius: tokens.radiusControl, fontSize: 12.5, fontWeight: 600,
                  cursor: 'pointer', fontFamily: tokens.font,
                  background: newType === t ? tokens.ai : tokens.surfaceSoft,
                  color: newType === t ? '#fff' : tokens.textSecondary,
                  border: '1px solid ' + (newType === t ? tokens.ai : tokens.border) }}>
                {t === 'dm' ? 'Direct' : 'Group'}</button>))}
          </div>
          {newType === 'group' && (
            <input value={newTitle} onChange={e => setNewTitle(e.target.value)} placeholder="Group name"
              style={{ width: '100%', boxSizing: 'border-box', border: '1px solid ' + tokens.border,
                borderRadius: tokens.radiusControl, padding: '8px 11px', fontSize: 13, marginBottom: 12,
                fontFamily: tokens.font }} />)}
          {facDropdown}
          <div style={{ maxHeight: 260, overflowY: 'auto', border: '1px solid ' + tokens.borderSoft,
            borderRadius: 8, marginBottom: 14 }}>
            {visibleUsers.map(u => {
              const sel = newMembers.includes(u.id);
              return (
                <div key={u.id} onClick={() => {
                  if (newType === 'dm') setNewMembers([u.id]);
                  else setNewMembers(m => sel ? m.filter(x => x !== u.id) : [...m, u.id]);
                }} style={{ padding: '8px 12px', fontSize: 13, cursor: 'pointer', color: tokens.ink,
                  background: sel ? tokens.aiSoft : 'transparent',
                  borderBottom: '1px solid ' + tokens.borderSoft }}>
                  {u.first_name} {u.last_name}
                  <span style={{ color: tokens.textMuted, fontSize: 11.5 }}> - {(u.role || '').replace('_', ' ')}{u.facility_name ? ' \u00b7 ' + u.facility_name : ''}</span>
                </div>);
            })}
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button onClick={() => setShowNew(false)} style={{ background: tokens.surface, color: tokens.textSecondary,
              border: '1px solid ' + tokens.border, borderRadius: tokens.radiusControl, padding: '8px 16px',
              fontSize: 12.5, cursor: 'pointer', fontFamily: tokens.font }}>Cancel</button>
            <button onClick={createThread} disabled={!newMembers.length}
              style={{ background: tokens.ai, color: '#fff', border: 'none', borderRadius: tokens.radiusControl,
                padding: '8px 16px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
                opacity: newMembers.length ? 1 : 0.5, fontFamily: tokens.font }}>Start</button>
          </div>
        </div>
      </div>)}
    </div>
  );
}
